import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";

const TYPE_TO_ROLE = { player: "player", community: "club", academy: "academy" };
const ROLE_TO_TYPE = { player: "player", club: "community", academy: "academy" };
const ROLE_FIELDS = ["position", "foot", "age", "orgName", "rep", "players", "teams", "ageGroups"];

const API = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.16, 1, 0.3, 1];

const LOGO_SRC =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_tfc-preview/artifacts/8wo6q5t4_64C5E782-E3B8-466C-A3C1-CAE785C8E0A5.jpg";

const TYPES = [
  { id: "player", title: "Player", sub: "For the ones on the pitch" },
  {
    id: "club",
    title: "Football Community / Club",
    sub: "For the ones building the game locally",
  },
  { id: "academy", title: "Academy", sub: "For the ones developing talent" },
];

const POSITIONS = ["Goalkeeper", "Defender", "Midfielder", "Attacker"];
const FEET = ["Left", "Right", "Both"];
const COUNTS = ["1-20", "21-50", "51-100", "101-200", "200+"];
const AGE_GROUPS = ["U-7", "U-9", "U-11", "U-13", "U-15", "U-17", "U-19", "Senior"];

const inputCls =
  "w-full rounded-md border border-[#2A2A2A] bg-[#0B0B0B] px-4 py-3 font-body text-base text-white placeholder-[#B3B3B3]/40 outline-none transition-colors duration-300 focus:border-[#1F7A4D]";

const ROLE_CONFIRM = {
  player: "Your player profile is officially in.",
  club: "Your community is officially part of the Collective.",
  academy: "Your academy is officially part of the Collective.",
};

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="font-body text-xs uppercase tracking-[0.18em] text-[#B3B3B3]">
        {label}
      </span>
      {children}
    </label>
  );
}

function Header() {
  return (
    <header className="border-b border-[#2A2A2A]">
      <div className="max-w-[1400px] mx-auto flex h-[76px] items-center justify-between px-6 md:px-12 lg:px-16">
        <Link to="/" data-testid="register-logo" className="flex items-center">
          <img
            src={LOGO_SRC}
            alt="The Football Collective"
            className="h-12 w-auto mix-blend-screen"
            draggable="false"
          />
        </Link>
        <Link
          to="/"
          data-testid="register-back-home"
          className="inline-flex items-center gap-2 font-body text-sm text-[#B3B3B3] transition-colors duration-300 hover:text-[#26945D]"
        >
          <ArrowLeft size={16} /> Back to the Collective
        </Link>
      </div>
    </header>
  );
}

export default function Register() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState("select"); // select | form | success
  const [role, setRole] = useState(null);
  const [data, setData] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Read ?type= on mount / when it changes (deep-link into a form)
  useEffect(() => {
    const param = searchParams.get("type");
    const mapped = param ? TYPE_TO_ROLE[param.toLowerCase()] : null;
    if (mapped) {
      setRole(mapped);
      setError("");
      setStep((s) => (s === "success" ? s : "form"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const clearRoleFields = (d) => {
    const next = { ...d };
    ROLE_FIELDS.forEach((f) => delete next[f]);
    return next; // keep shared fields (name, email, mobile)
  };

  const startRole = (id) => {
    setRole(id);
    // clear only role-specific fields, keep shared ones
    setData((d) => ({ ...clearRoleFields(d), ageGroups: [] }));
    setError("");
    setStep("form");
    setSearchParams({ type: ROLE_TO_TYPE[id] });
  };

  const changeType = () => {
    setError("");
    setStep("select");
    setSearchParams({});
  };

  const toggleGroup = (g) => {
    setData((d) => {
      const cur = d.ageGroups || [];
      return {
        ...d,
        ageGroups: cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g],
      };
    });
  };

  const validate = () => {
    const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((data.email || "").trim());
    if (!emailOk) return "Enter a valid email address.";
    if ((data.mobile || "").length !== 10) return "Enter a valid 10-digit mobile number.";
    if (role === "player") {
      if (!(data.name || "").trim()) return "Name is required.";
      if (!data.position) return "Select a position.";
      if (!data.foot) return "Select a preferred foot.";
      const age = Number(data.age);
      if (!age || age < 5 || age > 99) return "Enter a valid age.";
    } else {
      if (!(data.orgName || "").trim())
        return role === "club" ? "Community / Club name is required." : "Academy name is required.";
      if (!(data.rep || "").trim()) return "Representative name is required.";
      if (!data.players) return "Select the number of players.";
      if (role === "academy" && (data.ageGroups || []).length === 0)
        return "Select at least one age group.";
    }
    return "";
  };

  const submit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSubmitting(true);

    const payload = {
      role,
      email: (data.email || "").trim(),
      mobile: data.mobile,
      location: "Delhi",
    };
    if (role === "player") {
      Object.assign(payload, {
        name: data.name.trim(),
        position: data.position,
        preferred_foot: data.foot,
        age: Number(data.age),
      });
    } else {
      Object.assign(payload, {
        org_name: data.orgName.trim(),
        representative_name: data.rep.trim(),
        number_of_players: data.players,
      });
      if (role === "club" && data.teams) payload.number_of_teams = Number(data.teams);
      if (role === "academy") payload.age_groups = data.ageGroups;
    }

    try {
      const res = await fetch(`${API}/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.detail || "Something went wrong. Please try again.");
        setSubmitting(false);
        return;
      }
      setResult(body);
      setStep("success");
    } catch (e) {
      setError("Unable to reach the server. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="register-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <AnimatePresence mode="wait">
          {step === "select" && (
            <motion.div
              key="select"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
            >
              <span className="flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-[#1F7A4D]" />
                <span className="font-body text-xs md:text-sm font-medium tracking-[0.3em] uppercase text-[#B3B3B3]">
                  Registration
                </span>
              </span>
              <h1 className="mt-6 font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
                Join the Collective.
              </h1>
              <p className="mt-5 max-w-[46ch] font-body text-lg font-light text-[#B3B3B3]">
                Select how you want to join. You can add more roles to the same
                account later.
              </p>

              <p className="mt-12 font-body text-xs font-medium uppercase tracking-[0.3em] text-[#B3B3B3]">
                I Am A
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => startRole(t.id)}
                    data-testid={`type-${t.id}`}
                    className="flex h-full flex-col border border-[#2A2A2A] bg-[#0B0B0B] p-6 text-left transition-colors duration-300 outline-none hover:border-[#1F7A4D] hover:bg-[#151515] focus-visible:border-[#1F7A4D]"
                  >
                    <span className="font-display uppercase text-white text-2xl leading-[0.95] tracking-[0.02em]">
                      {t.title}
                    </span>
                    <span className="mt-3 font-body text-sm font-light text-[#B3B3B3]">
                      {t.sub}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              data-testid="register-form"
            >
              <button
                type="button"
                onClick={changeType}
                data-testid="form-back"
                className="inline-flex items-center gap-2 font-body text-sm text-[#B3B3B3] transition-colors hover:text-[#26945D]"
              >
                <ArrowLeft size={16} /> Change type
              </button>
              <h1 className="mt-6 font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.9]">
                {TYPES.find((t) => t.id === role)?.title}
              </h1>

              <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
                {role === "player" && (
                  <Field label="Name">
                    <input
                      className={inputCls}
                      data-testid="field-name"
                      value={data.name || ""}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder="Full name"
                    />
                  </Field>
                )}
                {role !== "player" && (
                  <>
                    <Field label={role === "club" ? "Community / Club Name" : "Academy Name"}>
                      <input
                        className={inputCls}
                        data-testid="field-org-name"
                        value={data.orgName || ""}
                        onChange={(e) => set("orgName", e.target.value)}
                        placeholder={role === "club" ? "e.g. Hauz Khas United FC" : "e.g. Saket Football Academy"}
                      />
                    </Field>
                    <Field label="Representative Name">
                      <input
                        className={inputCls}
                        data-testid="field-rep"
                        value={data.rep || ""}
                        onChange={(e) => set("rep", e.target.value)}
                        placeholder="Full name"
                      />
                    </Field>
                  </>
                )}

                <Field label="Email">
                  <input
                    type="email"
                    className={inputCls}
                    data-testid="field-email"
                    value={data.email || ""}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@example.com"
                  />
                </Field>

                <Field label="Mobile Number">
                  <div className="flex">
                    <span className="flex items-center rounded-l-md border border-r-0 border-[#2A2A2A] bg-[#151515] px-3.5 font-body text-base text-[#B3B3B3]">
                      +91
                    </span>
                    <input
                      inputMode="numeric"
                      className={`${inputCls} rounded-l-none`}
                      data-testid="field-mobile"
                      value={data.mobile || ""}
                      onChange={(e) =>
                        set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      placeholder="10-digit number"
                    />
                  </div>
                </Field>

                <Field label="Location">
                  <input
                    className={`${inputCls} text-[#B3B3B3]`}
                    data-testid="field-location"
                    value="Delhi"
                    readOnly
                  />
                </Field>

                {role === "player" && (
                  <>
                    <Field label="Position">
                      <select
                        className={inputCls}
                        data-testid="field-position"
                        value={data.position || ""}
                        onChange={(e) => set("position", e.target.value)}
                      >
                        <option value="">Select position</option>
                        {POSITIONS.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Preferred Foot">
                      <select
                        className={inputCls}
                        data-testid="field-foot"
                        value={data.foot || ""}
                        onChange={(e) => set("foot", e.target.value)}
                      >
                        <option value="">Select foot</option>
                        {FEET.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Age">
                      <input
                        inputMode="numeric"
                        className={inputCls}
                        data-testid="field-age"
                        value={data.age || ""}
                        onChange={(e) =>
                          set("age", e.target.value.replace(/\D/g, "").slice(0, 2))
                        }
                        placeholder="Age"
                      />
                    </Field>
                  </>
                )}

                {role !== "player" && (
                  <Field label="Number of Players">
                    <select
                      className={inputCls}
                      data-testid="field-players"
                      value={data.players || ""}
                      onChange={(e) => set("players", e.target.value)}
                    >
                      <option value="">Select range</option>
                      {COUNTS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                )}

                {role === "club" && (
                  <Field label="Number of Teams (optional)">
                    <input
                      inputMode="numeric"
                      className={inputCls}
                      data-testid="field-teams"
                      value={data.teams || ""}
                      onChange={(e) =>
                        set("teams", e.target.value.replace(/\D/g, "").slice(0, 3))
                      }
                      placeholder="e.g. 4"
                    />
                  </Field>
                )}
              </div>

              {role === "academy" && (
                <div className="mt-6">
                  <p className="font-body text-xs uppercase tracking-[0.18em] text-[#B3B3B3]">
                    Age Groups
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2.5" data-testid="field-age-groups">
                    {AGE_GROUPS.map((g) => {
                      const on = (data.ageGroups || []).includes(g);
                      return (
                        <button
                          key={g}
                          type="button"
                          onClick={() => toggleGroup(g)}
                          data-testid={`age-group-${g}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 font-body text-sm transition-colors duration-300 outline-none ${
                            on
                              ? "border-[#1F7A4D] bg-[#1F7A4D]/10 text-white"
                              : "border-[#2A2A2A] text-[#B3B3B3] hover:border-[#1F7A4D]"
                          }`}
                        >
                          {on && <Check size={14} className="text-[#1F7A4D]" />}
                          {g}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {error && (
                <p
                  data-testid="register-error"
                  className="mt-8 border-l-2 border-[#1F7A4D] bg-[#151515] px-4 py-3 font-body text-sm text-white"
                >
                  {error}
                </p>
              )}

              <div className="mt-10">
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="register-submit"
                  className="inline-flex items-center gap-2.5 rounded-full bg-[#1F7A4D] px-8 py-4 font-body text-base font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-[#26945D] hover:scale-[1.02] active:scale-100 disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Complete Registration"}
                </button>
              </div>
            </motion.div>
          )}

          {step === "success" && result && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              data-testid="register-success"
              className="max-w-[760px]"
            >
              {result.status === "role_added" ? (
                <>
                  <h1 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
                    New Role. Same Collective.
                  </h1>
                  <p className="mt-8 font-body text-lg font-light leading-relaxed text-[#B3B3B3]">
                    Your new role has been added successfully to your existing
                    account.
                  </p>
                  <p className="mt-4 font-body text-lg text-white">
                    {ROLE_CONFIRM[result.role]}
                  </p>
                </>
              ) : (
                <>
                  <h1 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
                    You&apos;re In. ⚽
                  </h1>
                  <div className="mt-8 space-y-4 font-body text-lg font-light leading-relaxed text-[#B3B3B3]">
                    <p>Welcome to The Football Collective.</p>
                    <p>
                      Your registration is complete, and your football journey
                      starts here.
                    </p>
                    <p>
                      You&apos;re now part of the community building the digital
                      home of grassroots football.
                    </p>
                    <p className="text-white">
                      Play more. Get recognised. Leave your mark.
                    </p>
                  </div>
                  <p className="mt-6 font-body text-lg text-[#1F7A4D]">
                    {ROLE_CONFIRM[result.role]}
                  </p>
                </>
              )}

              <Link
                to="/"
                data-testid="success-home"
                className="mt-12 inline-flex items-center gap-2.5 rounded-full bg-[#1F7A4D] px-8 py-4 font-body text-base font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-[#26945D] hover:scale-[1.02]"
              >
                Back to the Collective
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
